import { useEffect, useRef, useState } from "react";

/**
 * Treibt eine Sequenz von "Schritten" (jeder mit eigener Dauer) als
 * endlose Schleife an – für kleine, videoartige Produkt-Demos aus reinen
 * UI-Zuständen statt echtem Videomaterial. Pausiert außerhalb des
 * Viewports (IntersectionObserver) und respektiert prefers-reduced-motion
 * (zeigt dann dauerhaft den letzten Schritt).
 *
 * `steps` muss eine stabile Referenz sein (z.B. als Modul-Konstante
 * definiert), sonst startet der Zyklus bei jedem Render neu.
 */
export default function useAutoPlayLoop(steps) {
  const ref = useRef(null);
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setIndex(steps.length - 1);
      return undefined;
    }

    const advance = () => {
      const next = (indexRef.current + 1) % steps.length;
      indexRef.current = next;
      setIndex(next);
      timeoutRef.current = setTimeout(advance, steps[next].duration);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timeoutRef.current);
        if (entry.isIntersecting) {
          timeoutRef.current = setTimeout(advance, steps[indexRef.current].duration);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  return { ref, step: steps[index], index };
}
